import "./styles.scss";
import { useHistory } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { editAppAPI, deleteAppAPI, lookupAppStoreIconAPI } from "../api/ClientApi";
import { useDispatch } from "react-redux";
import { handleLogoutRedux } from "../../redux/actions/userAction";
import { Confirm } from "react-admin";

const APP_STORE_ID_RE = /\/id(\d+)/;
const isSupportedStoreUrl = (url) =>
  (url.includes("apps.apple.com") && APP_STORE_ID_RE.test(url)) ||
  (url.includes("play.google.com") && /[?&]id=/.test(url));

const decodeHtmlEntities = (text) => {
  if (text == null || text === "") return text;
  return String(text)
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)));
};

const EditApp = (props) => {
  const [appId, setAppId] = useState("");
  const [appName, setAppName] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [appUrl, setAppUrl] = useState("");
  const [os, setOS] = useState("ios");
  const [firebaseCredential, setFirebaseCredential] = useState("");
  const [hasFirebaseCredential, setHasFirebaseCredential] = useState(false);
  const [firebaseCredentialSandbox, setFirebaseCredentialSandbox] = useState("");
  const [hasFirebaseCredentialSandbox, setHasFirebaseCredentialSandbox] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLookingUpIcon, setIsLookingUpIcon] = useState(false);
  const containsWhitespace = (str) => /\s/.test(str);
  const lookupTimerRef = useRef(null);
  const lookupRequestIdRef = useRef(0);

  const defaultObjectCheck = {
    isValidAppID: true,
    isValidAppName: true,
    isValidBundleId: true,
    isValidIconUrl: true,
    isValidOS: true,
    isValidAppUrl: true,
  };
  const [objectCheckInput, setObjectCheckInput] = useState(defaultObjectCheck);
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    };
  }, []);

  const fetchIconFromStoreUrl = (url, { silentUnsupported = false } = {}) => {
    const trimmed = (url || "").trim();
    if (!isSupportedStoreUrl(trimmed)) {
      if (!silentUnsupported) {
        toast.error("URL không phải App Store hoặc Google Play hợp lệ");
      }
      return;
    }
    const requestId = ++lookupRequestIdRef.current;
    setIsLookingUpIcon(true);
    lookupAppStoreIconAPI({ url: trimmed })
      .then((response) => {
        if (requestId !== lookupRequestIdRef.current) {
          return;
        }
        const { errorCode, message, data } = response;
        if (errorCode === 0 && data && data.iconUrl) {
          setIconUrl(data.iconUrl);
          if (data.appName) {
            setAppName(decodeHtmlEntities(data.appName));
          }
          const storeLabel = data.store === "google_play" ? "Google Play" : "App Store";
          toast.success(
            data.appName
              ? `Đã lấy tên và icon từ ${storeLabel}`
              : `Đã lấy icon từ ${storeLabel}`
          );
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else if (errorCode && errorCode !== 0) {
          toast.error(message || "Không lấy được icon từ store");
        }
      })
      .catch(() => {
        if (requestId === lookupRequestIdRef.current) {
          toast.error("Không lấy được icon từ store");
        }
      })
      .finally(() => {
        if (requestId === lookupRequestIdRef.current) {
          setIsLookingUpIcon(false);
        }
      });
  };

  const handleSyncIcon = () => {
    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }
    fetchIconFromStoreUrl(appUrl);
  };

  const handleAppUrlChange = (value) => {
    setAppUrl(value);
    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }
    if (!isSupportedStoreUrl(value)) {
      return;
    }
    lookupTimerRef.current = setTimeout(() => {
      fetchIconFromStoreUrl(value.trim(), { silentUnsupported: true });
    }, 600);
  };

  const handleRegister = () => {
    let invalid = invalidInputs();
    if (invalid) {
      editAppAPI({
        appId,
        appName,
        bundleId,
        iconUrl,
        os,
        appUrl,
        firebaseCredential,
        firebaseCredentialSandbox,
      }).then((response) => {
        let { errorCode, message } = response;
        if (errorCode === 0) {
          toast.success(message);
          if (firebaseCredential) {
            setHasFirebaseCredential(true);
            setFirebaseCredential("");
          }
          if (firebaseCredentialSandbox) {
            setHasFirebaseCredentialSandbox(true);
            setFirebaseCredentialSandbox("");
          }
          history.push("/admin");
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      });
    }
  };

  const invalidInputs = () => {
    setObjectCheckInput(defaultObjectCheck);
    if (!appId) {
      toast.error("App id is not empty!");
      setObjectCheckInput({ ...defaultObjectCheck, isValidAppID: false });
      return false;
    }
    if (!appName) {
      toast.error("App name is not empty!");
      setObjectCheckInput({ ...defaultObjectCheck, isValidAppName: false });
      return false;
    }

    if (!bundleId) {
      toast.error("Bundle id is not empty!");
      setObjectCheckInput({
        ...defaultObjectCheck,
        isValidBundleId: false,
      });

      return false;
    }
    if (!os) {
      toast.error("OS is not empty!");
      setObjectCheckInput({ ...defaultObjectCheck, isValidOS: false });
      return false;
    }
    if (containsWhitespace(appId)) {
      toast.error("App id is not contain whitespace!");
      setObjectCheckInput({ ...defaultObjectCheck, isValidAppID: false });
      return false;
    }
    return true;
  };
  const handleDelete = () => {
    setShowDeleteConfirm(false);
    deleteAppAPI({ appId }).then((response) => {
      let { errorCode, message } = response;
      if (errorCode === 0) {
        toast.success(message);
        history.push("/admin");
      } else if (errorCode === 401) {
        toast.error(message);
        dispatch(handleLogoutRedux());
      } else {
        toast.error(message);
      }
    });
  };
  useEffect(() => {
    const {
      app_id,
      app_name,
      app_url,
      bundle_id,
      icon_url,
      is_active,
      os,
      has_firebase_credential,
      has_firebase_credential_sandbox,
    } = props.location.state;
    setAppId(app_id);
    setAppName(app_name);
    setAppUrl(app_url);
    setBundleId(bundle_id);
    setIconUrl(icon_url);
    setOS(os);
    setHasFirebaseCredential(!!has_firebase_credential);
    setHasFirebaseCredentialSandbox(!!has_firebase_credential_sandbox);
  }, []);
  return (
    <div className="page-container">
      <div className="auth-card auth-card--wide">
        <h1 className="page-title mb-1">Edit App</h1>
        <p className="page-subtitle mb-0">{appId}</p>

        <div className="form-section-title">Thông tin app</div>
        <div className="mb-3">
          <label className="form-label">App ID</label>
          <input
            className={
              objectCheckInput.isValidAppID
                ? "form-control"
                : "form-control is-invalid"
            }
            type="text"
            placeholder="App ID"
            value={appId}
            onChange={(event) => setAppId(event.target.value)}
            disabled
          />
        </div>
        <div className="mb-3">
          <label className="form-label">App Name</label>
          <input
            className={
              objectCheckInput.isValidAppName
                ? "form-control"
                : "form-control is-invalid"
            }
            type="text"
            placeholder="App Name"
            value={appName}
            onChange={(event) => setAppName(event.target.value)}
          />
        </div>
        <div className="row">
          <div className="col-md-8 mb-3">
            <label className="form-label">Bundle ID</label>
            <input
              className={
                objectCheckInput.isValidBundleId
                  ? "form-control"
                  : "form-control is-invalid"
              }
              type="text"
              placeholder="BundleId"
              value={bundleId}
              onChange={(event) => setBundleId(event.target.value)}
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">OS</label>
            <select
              className="form-select"
              value={os}
              onChange={(event) => {
                setOS(event.target.value);
              }}
            >
              <option value={"ios"}>iOS</option>
              <option value={"android"}>Android</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Icon URL</label>
          <input
            className={
              objectCheckInput.isValidIconUrl
                ? "form-control"
                : "form-control is-invalid"
            }
            type="text"
            placeholder="Icon url (tuỳ chọn)"
            value={iconUrl}
            onChange={(event) => setIconUrl(event.target.value)}
          />
          {isLookingUpIcon && (
            <div className="form-text">Đang lấy icon từ store…</div>
          )}
          {iconUrl && !isLookingUpIcon && (
            <div className="mt-2">
              <img
                src={iconUrl}
                alt="App icon preview"
                style={{ width: 64, height: 64, borderRadius: 12, objectFit: "cover" }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          )}
        </div>
        <div className="mb-3">
          <label className="form-label">App URL</label>
          <div className="input-group">
            <input
              className={
                objectCheckInput.isValidAppUrl
                  ? "form-control"
                  : "form-control is-invalid"
              }
              type="text"
              placeholder={
                os === "android"
                  ? "https://play.google.com/store/apps/details?id=..."
                  : "https://apps.apple.com/.../id123456789"
              }
              value={appUrl}
              onChange={(event) => handleAppUrlChange(event.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleSyncIcon}
              disabled={isLookingUpIcon || !appUrl}
              title="Lấy icon từ store URL"
            >
              {isLookingUpIcon ? "Đang lấy…" : "Sync info"}
            </button>
          </div>
          <div className="form-text">
            Dán App Store hoặc Google Play URL rồi bấm Sync info để lấy App Name + Icon URL.
          </div>
        </div>

        <div className="form-section-title d-flex align-items-center gap-2">
          Firebase credential — Production
          {hasFirebaseCredential ? (
            <span className="badge rounded-pill text-bg-success">Đã cấu hình</span>
          ) : (
            <span className="badge rounded-pill text-bg-secondary">Chưa cấu hình</span>
          )}
        </div>
        <div className="mb-4">
          <label className="form-label">Firebase Service Account JSON (Production)</label>
          <textarea
            className="form-control"
            placeholder="Để trống nếu không đổi"
            rows={5}
            value={firebaseCredential}
            onChange={(event) => setFirebaseCredential(event.target.value)}
          />
          <div className="form-text">
            Project Firebase thật, dùng khi gửi push cho user thật. Vì lý do bảo mật, giá trị đã
            lưu không được hiện lại ở đây — ô này chỉ dùng để{" "}
            {hasFirebaseCredential ? "thay thế credential hiện có" : "thêm credential mới"}. Để
            trống và bấm Save nếu không cần đổi.
          </div>
        </div>

        <div className="form-section-title d-flex align-items-center gap-2">
          Firebase credential — Sandbox
          {hasFirebaseCredentialSandbox ? (
            <span className="badge rounded-pill text-bg-success">Đã cấu hình</span>
          ) : (
            <span className="badge rounded-pill text-bg-secondary">Chưa cấu hình</span>
          )}
        </div>
        <div className="mb-4">
          <label className="form-label">Firebase Service Account JSON (Sandbox)</label>
          <textarea
            className="form-control"
            placeholder="Để trống nếu không đổi"
            rows={5}
            value={firebaseCredentialSandbox}
            onChange={(event) => setFirebaseCredentialSandbox(event.target.value)}
          />
          <div className="form-text">
            Project Firebase riêng cho test/nội bộ (tuỳ chọn) — chọn "Sandbox" khi gửi push để
            dùng project này thay vì production. Để trống và bấm Save nếu không cần đổi.
          </div>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-primary flex-fill" onClick={() => handleRegister()}>
            Save
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => history.push("/admin")}
          >
            Huỷ
          </button>
        </div>

        <div className="form-section-title text-danger">Vùng nguy hiểm</div>
        <div className="d-flex align-items-center justify-content-between gap-3">
          <p className="page-subtitle mb-0">
            Xoá app sẽ gỡ khỏi danh sách quản lý. Dữ liệu thiết bị/lịch sử push liên quan sẽ không
            bị xoá theo.
          </p>
          <button
            type="button"
            className="btn btn-outline-danger flex-shrink-0"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Xoá app
          </button>
        </div>
      </div>

      <Confirm
        isOpen={showDeleteConfirm}
        loading={false}
        title=""
        content={`Xác nhận xoá app "${appId}"? Hành động này không thể hoàn tác.`}
        confirm="Xoá"
        confirmColor="warning"
        cancel="Huỷ bỏ"
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default EditApp;
